import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAuditLogs, getAuditLogStats } from '../../services/api';
import { 
  FileText, RefreshCw, Calendar, User, Filter, Search, 
  ChevronLeft, ChevronRight, Eye, Clock, AlertCircle,
  CheckCircle, XCircle, Edit, Trash2, Plus, Shield, X
} from 'lucide-react';
import toast from 'react-hot-toast';
const AdminAuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const navigate = useNavigate();

  // Filters
  const [filters, setFilters] = useState({
    adminUsername: '',
    entityType: 'all',
    action: 'all',
    startDate: '',
    endDate: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1
  });

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('ifm_admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      await refresh();
    };
    init();
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [pagination.page, filters]);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'all') delete params[key];
      });

      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        queryParams.append(key, params[key]);
      });
      const queryString = queryParams.toString();

      const data = await listAuditLogs(queryString ? `?${queryString}` : '');
      
      setLogs(data.logs || []);
      setPagination({
        ...pagination,
        total: data.total || 0,
        totalPages: data.totalPages || 1
      });
    } catch (e) {
      const errorMsg = e?.data?.message || e?.message || 'Failed to load audit logs';
      toast.error(errorMsg);
      setError(errorMsg);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'update':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 text-red-600" />;
      case 'approve':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'reject':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'activate':
        return <Shield className="h-4 w-4 text-green-600" />;
      case 'deactivate':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create':
      case 'approve':
      case 'activate':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'delete':
      case 'reject':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'update':
      case 'status_change':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'deactivate':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateString);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-7 w-7 text-primary-600" />
                Activity Logs
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Track all admin actions and changes in the system
              </p>
            </div>
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="px-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
              {Object.values(filters).some(f => f && f !== 'all') && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f && f !== 'all').length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f && f !== 'all') && (
              <button
                onClick={() => {
                  setFilters({
                    adminUsername: '',
                    entityType: 'all',
                    action: 'all',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Clear All
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Admin Username Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Admin Username
              </label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search username..."
                  value={filters.adminUsername}
                  onChange={(e) => handleFilterChange('adminUsername', e.target.value)}
                  className={`w-full pl-9 ${filters.adminUsername ? 'pr-9' : 'pr-3'} py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                />
                {filters.adminUsername && (
                  <button
                    onClick={() => handleFilterChange('adminUsername', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Entity Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Entity Type
              </label>
              <select
                value={filters.entityType}
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value="all">All Types</option>
                <option value="Member">Member</option>
                <option value="Event">Event</option>
                <option value="News">News</option>
                <option value="Payment">Payment</option>
                <option value="EventRegistration">Event Registration</option>
                <option value="Grievance">Grievance</option>
                <option value="ContactSubmission">Contact</option>
                <option value="Photo">Photo</option>
                <option value="Video">Video</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            {/* Action Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value="all">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="status_change">Status Change</option>
                <option value="payment_update">Payment Update</option>
              </select>
            </div>

            {/* Start Date Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Start Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
              </div>
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                End Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Loading audit logs...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
            <button
              onClick={refresh}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No audit logs found</p>
            <p className="text-gray-500 text-sm">
              {Object.values(filters).some(f => f && f !== 'all')
                ? 'Try adjusting your filters'
                : 'Activity logs will appear here as admins perform actions'}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {log.action ? log.action.replace(/_/g, ' ') : 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.entityType}
                            </div>
                            <div className="text-sm text-gray-500">
                              {log.entityName || (log.entityId ? log.entityId.toString().substring(0, 8) : 'N/A')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.adminUsername || 'Unknown'}
                            </div>
                            {log.adminEmail && (
                              <div className="text-sm text-gray-500">
                                {log.adminEmail}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-md truncate">
                            {log.description || 'No description'}
                          </div>
                          {log.reason && (
                            <div className="text-xs text-gray-500 mt-1">
                              Reason: {log.reason}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(log.createdAt)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatTimeAgo(log.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            log.status === 'success' 
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : log.status === 'failed'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}>
                            {log.status || 'success'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(log)}
                            className="text-primary-600 hover:text-primary-900 flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {[...Array(pagination.totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.totalPages ||
                          (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pagination.page === pageNum
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                          return (
                            <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-end z-[1000]">
          <div className="bg-white shadow-xl w-full sm:w-[92vw] md:w-[82vw] lg:w-[70vw] xl:w-[60vw] 2xl:w-[52vw] max-w-[960px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-y-auto overflow-x-hidden rounded-none sm:rounded-2xl min-w-0">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Audit Log Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Action</label>
                  <div className="mt-1 flex items-center gap-2">
                    {getActionIcon(selectedLog.action)}
                    <span className="text-lg font-semibold text-gray-900 capitalize">
                      {selectedLog.action ? selectedLog.action.replace(/_/g, ' ') : 'Unknown'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                      selectedLog.status === 'success' 
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : selectedLog.status === 'failed'
                        ? 'bg-red-100 text-red-800 border-red-200'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }`}>
                      {selectedLog.status || 'success'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entity Type</label>
                  <div className="mt-1 text-lg font-semibold text-gray-900">
                    {selectedLog.entityType}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entity Name</label>
                  <div className="mt-1 text-lg font-semibold text-gray-900">
                    {selectedLog.entityName || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Admin Info */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Admin Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Username</label>
                    <div className="mt-1 text-gray-900">{selectedLog.adminUsername}</div>
                  </div>
                  {selectedLog.adminEmail && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <div className="mt-1 text-gray-900">{selectedLog.adminEmail}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedLog.description && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                  <p className="text-gray-700">{selectedLog.description}</p>
                </div>
              )}

              {/* Reason */}
              {selectedLog.reason && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Reason</h3>
                  <p className="text-gray-700">{selectedLog.reason}</p>
                </div>
              )}

              {/* Changes */}
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Changes Made</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {Object.entries(selectedLog.changes).map(([key, change]) => {
                      // Handle both object format {old: x, new: y} and direct value format
                      const oldValue = change && typeof change === 'object' && 'old' in change ? change.old : change;
                      const newValue = change && typeof change === 'object' && 'new' in change ? change.new : change;
                      
                      return (
                        <div key={key} className="flex items-start gap-4">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-700 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div className="mt-1 text-sm text-gray-600">
                              <span className="line-through text-red-600 mr-2">
                                {typeof oldValue === 'object' && oldValue !== null ? JSON.stringify(oldValue) : String(oldValue ?? 'N/A')}
                              </span>
                              <span className="text-green-600">
                                → {typeof newValue === 'object' && newValue !== null ? JSON.stringify(newValue) : String(newValue ?? 'N/A')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Request Info */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Request Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">IP Address</label>
                    <div className="mt-1 text-gray-900">{selectedLog.ipAddress || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Method</label>
                    <div className="mt-1 text-gray-900">{selectedLog.requestMethod || 'N/A'}</div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">URL</label>
                    <div className="mt-1 text-gray-900 break-all">{selectedLog.requestUrl || 'N/A'}</div>
                  </div>
                  {selectedLog.userAgent && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-gray-500">User Agent</label>
                      <div className="mt-1 text-gray-900 text-sm break-all">{selectedLog.userAgent}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamp */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {formatDate(selectedLog.createdAt)} ({formatTimeAgo(selectedLog.createdAt)})
                  </span>
                </div>
              </div>

              {/* Error Message */}
              {selectedLog.errorMessage && (
                <div className="border-t border-red-200 pt-4">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">Error Message</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">{selectedLog.errorMessage}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 w-full sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogsPage;

